import { Currency } from "@redotech/money/currencies";
import {
  MoneyValue,
  moneyValueToNumber,
} from "@redotech/money/money-calculator";
import { assertNever } from "@redotech/util/type";
import { AdjustmentType } from "../../return";
import { ProductTotals, ReturnTotals } from "../../return-totals-calculator";
import {
  ProductSummaryTotals,
  ReturnSummaryTotals,
  SerializedProductSummaryTotals,
  SerializedReturnSummaryTotals,
} from "./types";

export const RAC_LOG_STAMP = "RAC DARK LAUNCH";

export enum ReturnTotalsType {
  LEGACY = "legacy",
  NEW = "new",
}

const fieldsMatch = (
  legacyField: number,
  newField: number | MoneyValue<Currency>,
): boolean => {
  const newFieldValue =
    typeof newField === "number" ? newField : newFieldValueToNumber(newField);
  // Only log out differences greater than 0.05, the ones I've checked that are less than 0.05, RAC is correct
  // and RTC is wrong
  return Math.abs(legacyField - newFieldValue) < 0.05;
};

const newFieldValueToNumber = (
  newField: number | MoneyValue<Currency>,
): number => {
  return typeof newField === "number" ? newField : moneyValueToNumber(newField);
};

export const compareReturnTotals = (returnTotalsSet: ReturnTotalsSet) => {
  const discrepancies: {
    legacyField: { value: number; name: string };
    newField: { value: number; name: string };
    difference: number;
  }[] = [];
  const returnFieldsToCompare: (keyof ReturnTotals)[] = [
    "totalReturnValue",
    "totalStoreCredit",
    "totalRefundWithoutShipping",
    "totalRawReturnValue",
    "newOrderValue",
    "rawNewOrderValue",
    "displayCharge",
    "nonAccruableCredit",
    "totalStoreCreditWithShipping",
    "totalRefundWithShipping",
    "totalAdjustmentValue",
    "totalMerchantAdjustment",
    "totalDiscount",
    "totalNewOrderTaxes",
    "advancedExchangeItemsTaxInNewOrder",
    "shippingFee",
    "shippingFeeDeducted",
    "shippingFeeDeductedFromStoreCredit",
    "shippingFeeDeductedFromRefund",
    "totalTaxes",
    "nonAccruableLeftover",
    "finalSaleCredit",
    "totalOrderShipping",
    "totalPaymentOwed",
    "exchangeShippingFee",
    "customerLabelUpchargeFee",
  ];

  for (const field of returnFieldsToCompare) {
    const legacyValue = returnTotalsSet.legacy[field] as number;
    const newField = mapOldReturnFieldToNewField(field, returnTotalsSet);
    if (!newField) {
      continue;
    }
    if (!fieldsMatch(legacyValue, newField.value)) {
      discrepancies.push({
        legacyField: { value: legacyValue, name: field },
        newField: {
          value: newFieldValueToNumber(newField.value),
          name: newField.name,
        },
        difference: Math.abs(
          legacyValue - newFieldValueToNumber(newField.value),
        ),
      });
    }
  }

  const productFieldsToCompare: (keyof ProductTotals)[] = [
    "discountPrice",
    "originalPrice",
    "priceAdjustment",
    "merchantAdjustment",
    "finalPrice",
    "tax",
    "totalDiscount",
    "dynamicBundleAdjustment",
  ];

  for (const productTotals of returnTotalsSet.legacy.productTotals) {
    const newProductTotals = returnTotalsSet.new?.productTotalsList.find(
      (p) => p.id === productTotals.product._id?.toString(),
    );
    for (const field of productFieldsToCompare) {
      const legacyValue = productTotals[field] as number;
      const newField = mapOldProductFieldToNewField(field, {
        legacy: productTotals,
        new: newProductTotals,
        typeToUse: returnTotalsSet.typeToUse,
      });
      if (!newField) {
        continue;
      }
      if (!fieldsMatch(legacyValue, newField.value)) {
        discrepancies.push({
          legacyField: { value: legacyValue, name: field },
          newField: {
            value: newFieldValueToNumber(newField.value),
            name: newField.name,
          },
          difference: Math.abs(
            legacyValue - newFieldValueToNumber(newField.value),
          ),
        });
      }
    }
  }
  return discrepancies;
};

// ------------ Return Totals Set ------------ //

type NewReturnTotals =
  | ReturnSummaryTotals<Currency>
  | SerializedReturnSummaryTotals;

// Allows us to dark launch and hide return accounting calculator behind a feature flag
export type ReturnTotalsSet = {
  legacy: ReturnTotals;
  new: NewReturnTotals | undefined;
  typeToUse: ReturnTotalsType;
};

export const mapOldReturnFieldToNewField = (
  legacyFieldName: keyof ReturnTotals,
  returnTotalsSet: ReturnTotalsSet,
): { name: string; value: number | MoneyValue<Currency> } | null => {
  if (!returnTotalsSet.new) {
    return null;
  }
  switch (legacyFieldName) {
    case "totalReturnValue":
      return {
        name: "returnValue.amount",
        value: newFieldValueToNumber(returnTotalsSet.new.returnValue.amount),
      };
    case "totalStoreCredit":
      return {
        name: "storeCredit.withoutOriginalOrderShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.storeCredit.withoutOriginalOrderShipping,
        ),
      };
    case "totalRefundWithoutShipping":
      return {
        name: "refund.withoutOriginalOrderShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.refund.withoutOriginalOrderShipping,
        ),
      };
    case "totalRawReturnValue":
      return {
        name: "returnValue.preTaxPreAdjustments",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnValue.preTaxPreAdjustments,
        ),
      };
    case "newOrderValue":
      return {
        name: "newOrderTotals.totalValue",
        value: newFieldValueToNumber(
          returnTotalsSet.new.newOrderTotals.totalValue,
        ),
      };
    case "rawNewOrderValue":
      return {
        name: "newOrderTotals.preTax",
        value: newFieldValueToNumber(returnTotalsSet.new.newOrderTotals.preTax),
      };
    case "displayCharge":
      return {
        name: "outstandingBalance",
        value: newFieldValueToNumber(returnTotalsSet.new.outstandingBalance),
      };
    case "nonAccruableCredit":
      return {
        name: "preDiscountShopNowBonus",
        value: newFieldValueToNumber(
          returnTotalsSet.new.preDiscountShopNowBonus.total,
        ),
      };
    case "totalStoreCreditWithShipping":
      return {
        name: "totalStoreCreditWithShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.storeCredit.withOriginalOrderShipping,
        ),
      };
    case "totalRefundWithShipping":
      return {
        name: "refund.withOriginalOrderShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.refund.withOriginalOrderShipping,
        ),
      };
    // Non-numeric fields that should be handled separately
    case "productTotals":
    case "merchantAdjustmentsShouldBeAddedToSCRefund":
    case "exchangeShippingName":
      return null;
    // Numeric fields that don't have a mapping yet
    case "totalAdjustmentValue":
      return {
        name: "totalNetAdjustmentValue",
        value: newFieldValueToNumber(
          returnTotalsSet.new.adjustmentTotals.totalNetAdjustmentValue,
        ),
      };
    case "totalMerchantAdjustment":
      return {
        name: "totalNetMerchantManualAdjustment",
        value: newFieldValueToNumber(
          returnTotalsSet.new.adjustmentTotals.totalNetMerchantManualAdjustment,
        ),
      };
    case "totalDiscount":
      return {
        name: "returnValue.totalDiscounts",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnValue.totalDiscount,
        ),
      };
    case "newOrderDiscount":
      return {
        name: "newOrderTotals.totalDiscount",
        value: newFieldValueToNumber(
          returnTotalsSet.new.newOrderTotals.totalDiscount,
        ),
      };
    case "totalNewOrderTaxes":
      return {
        name: "newOrderTotals.taxes.total",
        value: newFieldValueToNumber(
          returnTotalsSet.new.newOrderTotals.taxes.total,
        ),
      };
    case "advancedExchangeItemsTaxInNewOrder":
      return {
        name: "newOrderTotals.taxes.forAdvancedExchangeProducts",
        value: newFieldValueToNumber(
          returnTotalsSet.new.newOrderTotals.taxes.forAdvancedExchangeProducts,
        ),
      };
    case "shippingFee":
      return {
        name: "returnShippingFee.amountPaidWithCard",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnShippingFee.amountPaidWithCard,
        ),
      };
    case "shippingFeeDeducted":
      return {
        name: "returnShippingFee.amountDeducted.fromStoreCreditAndRefund",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnShippingFee.amountDeducted
            .fromStoreCreditAndRefund,
        ),
      };
    case "shippingFeeDeductedFromStoreCredit":
      return {
        name: "returnShippingFee.amountDeducted.fromStoreCredit",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnShippingFee.amountDeducted.fromStoreCredit,
        ),
      };
    case "shippingFeeDeductedFromRefund":
      return {
        name: "returnShippingFee.amountDeducted.fromRefund",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnShippingFee.amountDeducted.fromRefund,
        ),
      };
    case "totalTaxes":
      return {
        name: "returnValue.totalTaxes",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnValue.totalTaxes,
        ),
      };
    case "nonAccruableLeftover":
      return {
        name: "preDiscountShopNowBonus.unused",
        value: newFieldValueToNumber(
          returnTotalsSet.new.preDiscountShopNowBonus.unused,
        ),
      };
    case "finalSaleCredit":
      return {
        name: "finalSaleCredit",
        value: newFieldValueToNumber(returnTotalsSet.new.finalSaleCredit),
      };
    case "totalOrderShipping":
      return {
        name: "originalOrderShippingFee",
        value: newFieldValueToNumber(
          returnTotalsSet.new.originalOrderShippingFee,
        ),
      };
    case "totalPaymentOwed":
      return {
        name: "totalPaymentOwed",
        value: newFieldValueToNumber(returnTotalsSet.new.totalPaymentOwed),
      };
    case "exchangeShippingFee":
      return {
        name: "newOrderTotals.shippingFee",
        value: newFieldValueToNumber(
          returnTotalsSet.new.newOrderTotals.exchangeShippingFee,
        ),
      };
    case "customerLabelUpchargeFee":
      return {
        name: "adjustmentTotals.totalCustomerLabelUpchargeFee",
        value: newFieldValueToNumber(
          returnTotalsSet.new.adjustmentTotals.totalCustomerLabelUpchargeFee,
        ),
      };
    // Fields that I'm going to deprecate
    case "advancedExchangeItemsValue":
    case "totalRawTaxes":
    case "charge":
    case "recoveryFee":
    case "totalLoyaltyPointsRefunded":
    case "totalLoyaltyPointsDeducted":
    case "totalReturnValueDeductedDueToLoyaltyPoints":
      return {
        name: legacyFieldName,
        value: returnTotalsSet.legacy[legacyFieldName],
      };
    default:
      assertNever(legacyFieldName);
  }
};

const getSingleFieldFromReturnTotalsSet = ({
  returnTotalsSet,
  legacyFieldName,
}: {
  returnTotalsSet: ReturnTotalsSet;
  legacyFieldName: keyof ReturnTotals;
}): number => {
  const legacyField = returnTotalsSet.legacy[legacyFieldName] as number;
  if (!returnTotalsSet.new) {
    return legacyField;
  }
  const newField = mapOldReturnFieldToNewField(
    legacyFieldName,
    returnTotalsSet,
  );
  switch (returnTotalsSet.typeToUse) {
    case ReturnTotalsType.LEGACY:
      return legacyField;
    case ReturnTotalsType.NEW:
      if (!newField) {
        throw new Error(
          `New field not found for legacy field ${legacyFieldName}`,
        );
      }
      return newFieldValueToNumber(newField.value);
    default:
      assertNever(returnTotalsSet.typeToUse);
  }
};

export const getFieldsFromReturnTotalsSet = ({
  returnTotalsSet,
}: {
  returnTotalsSet: ReturnTotalsSet;
}): ReturnTotals => {
  return {
    productTotals: returnTotalsSet.legacy.productTotals.map(
      (legacyProductTotals) => {
        const productId = legacyProductTotals.product._id?.toString();
        if (!productId) {
          return legacyProductTotals;
        }
        const productTotals = getProductFieldsFromReturnTotalsSet({
          returnTotalsSet,
          productId,
        });
        if (!productTotals) {
          return legacyProductTotals;
        }
        return productTotals;
      },
    ),
    totalStoreCredit: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalStoreCredit",
    }),
    totalStoreCreditWithShipping: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalStoreCreditWithShipping",
    }),
    totalRefundWithoutShipping: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalRefundWithoutShipping",
    }),
    totalRefundWithShipping: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalRefundWithShipping",
    }),
    totalReturnValue: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalReturnValue",
    }),
    totalRawReturnValue: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalRawReturnValue",
    }),
    totalAdjustmentValue: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalAdjustmentValue",
    }),
    totalMerchantAdjustment: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalMerchantAdjustment",
    }),
    totalDiscount: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalDiscount",
    }),
    newOrderValue: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "newOrderValue",
    }),
    rawNewOrderValue: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "rawNewOrderValue",
    }),
    newOrderDiscount: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "newOrderDiscount",
    }),
    totalNewOrderTaxes: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalNewOrderTaxes",
    }),
    advancedExchangeItemsTaxInNewOrder: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "advancedExchangeItemsTaxInNewOrder",
    }),
    totalTaxes: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalTaxes",
    }),
    totalRawTaxes: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalRawTaxes",
    }),
    charge: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "charge",
    }),
    displayCharge: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "displayCharge",
    }),
    recoveryFee: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "recoveryFee",
    }),
    shippingFee: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "shippingFee",
    }),
    shippingFeeDeducted: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "shippingFeeDeducted",
    }),
    shippingFeeDeductedFromStoreCredit: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "shippingFeeDeductedFromStoreCredit",
    }),
    shippingFeeDeductedFromRefund: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "shippingFeeDeductedFromRefund",
    }),
    nonAccruableCredit: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "nonAccruableCredit",
    }),
    nonAccruableLeftover: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "nonAccruableLeftover",
    }),
    finalSaleCredit: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "finalSaleCredit",
    }),
    totalOrderShipping: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalOrderShipping",
    }),
    totalPaymentOwed: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalPaymentOwed",
    }),
    merchantAdjustmentsShouldBeAddedToSCRefund:
      returnTotalsSet.legacy.merchantAdjustmentsShouldBeAddedToSCRefund,
    totalLoyaltyPointsRefunded: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalLoyaltyPointsRefunded",
    }),
    totalLoyaltyPointsDeducted: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "totalLoyaltyPointsDeducted",
    }),
    totalReturnValueDeductedDueToLoyaltyPoints:
      getSingleFieldFromReturnTotalsSet({
        returnTotalsSet,
        legacyFieldName: "totalReturnValueDeductedDueToLoyaltyPoints",
      }),
    exchangeShippingFee: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "exchangeShippingFee",
    }),
    exchangeShippingName: returnTotalsSet.legacy.exchangeShippingName,
    customerLabelUpchargeFee: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "customerLabelUpchargeFee",
    }),
    advancedExchangeItemsValue: getSingleFieldFromReturnTotalsSet({
      returnTotalsSet,
      legacyFieldName: "advancedExchangeItemsValue",
    }),
  };
};

// ------------ Product Totals Set ------------ //

type NewProductTotals =
  | SerializedProductSummaryTotals
  | ProductSummaryTotals<Currency>;
export type ProductTotalsSet = {
  legacy: ProductTotals;
  new: NewProductTotals | undefined;
  typeToUse: ReturnTotalsType;
};

const mapOldProductFieldToNewField = (
  legacyFieldName: keyof ProductTotals,
  productTotalsSet: ProductTotalsSet,
): { name: string; value: number | MoneyValue<Currency> } | null => {
  if (!productTotalsSet.new) {
    return null;
  }
  switch (legacyFieldName) {
    case "finalPrice":
      return {
        name: "returnValue.amount",
        value: newFieldValueToNumber(productTotalsSet.new.returnValue.amount),
      };
    case "discountPrice":
      return {
        name: "originalValue.discountedPrice",
        value: newFieldValueToNumber(
          productTotalsSet.new.originalValue.discountedPrice,
        ),
      };
    case "originalPrice":
      return {
        name: "originalValue.price",
        value: newFieldValueToNumber(productTotalsSet.new.originalValue.price),
      };
    case "priceAdjustment":
      return {
        name: "adjustments.returnFlow",
        value: newFieldValueToNumber(
          productTotalsSet.new.adjustments.find(
            (adjustment) =>
              adjustment.type === AdjustmentType.CHOOSE_ITEMS_FLOW,
          )?.amount ?? 0,
        ),
      };
    case "merchantAdjustment":
      return {
        name: "adjustments.merchantManual",
        value: newFieldValueToNumber(
          productTotalsSet.new.adjustments.find(
            (adjustment) => adjustment.type === AdjustmentType.MERCHANT_MANUAL,
          )?.amount ?? 0,
        ),
      };
    case "tax":
      return {
        name: "originalValue.tax",
        value: newFieldValueToNumber(productTotalsSet.new.originalValue.tax),
      };
    case "totalDiscount":
      return {
        name: "originalValue.discount",
        value: newFieldValueToNumber(
          productTotalsSet.new.originalValue.discount,
        ),
      };
    case "dynamicBundleAdjustment":
      return {
        name: "adjustments.dynamicBundle",
        value: newFieldValueToNumber(
          productTotalsSet.new.adjustments.find(
            (adjustment) =>
              adjustment.type === AdjustmentType.DYNAMIC_BUNDLE_ADJUSTMENT,
          )?.amount ?? 0,
        ),
      };
    // Non-numeric fields that should be handled separately
    case "product":
    case "refund_currency":
      return null;
    // Fields that I'm going to deprecate
    case "rawTax":
    case "returnValueDeductedDueToLoyaltyPoints":
    case "loyaltyPointsRefunded":
    case "loyaltyPointsDeducted":
    case "total_refund":
    case "refund_tax":
    case "refund_item":
      return {
        name: legacyFieldName,
        value: productTotalsSet.legacy[legacyFieldName],
      };
    default:
      assertNever(legacyFieldName);
  }
};

const getSingleFieldFromProductTotalsSet = ({
  productTotalsSet,
  legacyFieldName,
}: {
  productTotalsSet: ProductTotalsSet;
  legacyFieldName: keyof ProductTotals;
}): number => {
  const legacyField = productTotalsSet.legacy[legacyFieldName] as number;
  if (!productTotalsSet.new) {
    return legacyField;
  }
  const newField = mapOldProductFieldToNewField(
    legacyFieldName,
    productTotalsSet,
  );
  switch (productTotalsSet.typeToUse) {
    case ReturnTotalsType.LEGACY:
      return legacyField;
    case ReturnTotalsType.NEW:
      if (!newField) {
        throw new Error(
          `New field not found for legacy field ${legacyFieldName} for product ${productTotalsSet.legacy.product._id}`,
        );
      }
      return newFieldValueToNumber(newField.value);
    default:
      assertNever(productTotalsSet.typeToUse);
  }
};

export const getProductFieldsFromReturnTotalsSet = ({
  returnTotalsSet,
  productId,
  logErrorFn = console.error,
}: {
  returnTotalsSet: ReturnTotalsSet;
  productId: string;
  logErrorFn?: (message: string) => void;
}): ProductTotals | undefined => {
  const legacyProductTotals = returnTotalsSet.legacy.productTotals.find(
    (p) => p.product._id?.toString() === productId?.toString(),
  );
  if (!legacyProductTotals) {
    logErrorFn(
      `${RAC_LOG_STAMP} Legacy product totals not found for product ${productId}`,
    );
    return undefined;
  }
  const newProductTotals = returnTotalsSet.new?.productTotalsList.find(
    (p) => p.id.toString() === productId.toString(),
  );
  if (!newProductTotals) {
    logErrorFn(
      `${RAC_LOG_STAMP} New product totals not found for product ${productId}`,
    );
    if (returnTotalsSet.typeToUse === ReturnTotalsType.NEW) {
      throw new Error(`New product totals not found for product ${productId}`);
    }
  }
  const productTotalsSet = {
    legacy: legacyProductTotals,
    new: newProductTotals,
    typeToUse: returnTotalsSet.typeToUse,
  };

  return {
    product: legacyProductTotals.product,
    discountPrice: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "discountPrice",
    }),
    originalPrice: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "originalPrice",
    }),
    finalPrice: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "finalPrice",
    }),
    priceAdjustment: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "priceAdjustment",
    }),
    merchantAdjustment: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "merchantAdjustment",
    }),
    dynamicBundleAdjustment: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "dynamicBundleAdjustment",
    }),
    tax: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "tax",
    }),
    rawTax: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "rawTax",
    }),
    totalDiscount: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "totalDiscount",
    }),
    returnValueDeductedDueToLoyaltyPoints: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "returnValueDeductedDueToLoyaltyPoints",
    }),
    loyaltyPointsRefunded: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "loyaltyPointsRefunded",
    }),
    loyaltyPointsDeducted: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "loyaltyPointsDeducted",
    }),
    total_refund: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "total_refund",
    }),
    refund_tax: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "refund_tax",
    }),
    refund_item: getSingleFieldFromProductTotalsSet({
      productTotalsSet,
      legacyFieldName: "refund_item",
    }),
    refund_currency: legacyProductTotals.refund_currency,
  };
};
